import type { ReactNode } from "react";

import { useCurrentUser, useRoles } from "@/auth/queries";
import { ALL_PERMISSIONS, type PermissionSet } from "@/auth/permissions";
import { PermissionProvider } from "@/auth/PermissionProvider";
import { useSession } from "@/auth/session";
import { LIVE } from "@/lib/live";

/**
 * Supplies the permission set the shell filters navigation with (M15-5).
 *
 * Two sources, chosen the same way `auth/backend.ts` chooses between a real
 * session and the demo one: against the API, `CurrentUser.permissions` from
 * `GET /api/me` is a signed-in user's own permissions. On fixtures there is
 * no session to ask, so the demo persona stands in — see `FixturePermissions`
 * below, which is what makes `visibleModules` (#232) demonstrable at all.
 *
 * Hiding is never the enforcement either way (FR-ACL-8): the API refuses
 * regardless of what this renders.
 */
export function AppPermissions({ children }: { children: ReactNode }) {
  return LIVE ? (
    <LivePermissions>{children}</LivePermissions>
  ) : (
    <FixturePermissions>{children}</FixturePermissions>
  );
}

/**
 * Falls back to `ALL_PERMISSIONS` while `/api/me` is in flight or has not
 * resolved yet — an empty set would collapse the sidebar to nothing, which
 * reads as broken rather than loading, and the route guard is what actually
 * keeps a signed-out visitor off the page.
 *
 * "All" is the navigation's keys, not the catalogue: a permission only a
 * screen checks — `comments.create`, `orders.detail.cancel` — is *not* in it.
 * So the fallback must only ever be what is shown for the moment before the
 * answer arrives. If it outlives that, every such control is hidden from
 * everyone, Administrators included (#498) — which is why signing in has to
 * reach this component (`auth/session-cache.ts`).
 */
function LivePermissions({ children }: { children: ReactNode }) {
  const currentUserQuery = useCurrentUser();
  const permissions: PermissionSet = currentUserQuery.data
    ? new Set(currentUserQuery.data.permissions)
    : ALL_PERMISSIONS;

  return (
    <PermissionProvider permissions={permissions}>
      {children}
    </PermissionProvider>
  );
}

/**
 * Turns the demo persona into a permission set from
 * `SettingsRepository.listRoles()`. Swapping personas (`PersonaSwitcher`) is
 * what makes a warehouse operator's four-module sidebar something someone
 * can click instead of a claim in the spec.
 *
 * Falls back to every permission while the roles query is in flight, or if
 * `activeRoleId` names a role `listRoles` did not return — same reasoning as
 * `LivePermissions` above.
 */
function FixturePermissions({ children }: { children: ReactNode }) {
  const { activeRoleId } = useSession();
  const rolesQuery = useRoles();

  const activeRole = rolesQuery.data?.items.find(
    (role) => role.id === activeRoleId,
  );
  const permissions: PermissionSet = activeRole
    ? new Set(activeRole.permissions)
    : ALL_PERMISSIONS;

  return (
    <PermissionProvider permissions={permissions}>
      {children}
    </PermissionProvider>
  );
}
